import React, { useState } from 'react'
import Drawer from '@components/Drawer/Drawer.component'
import { OverlayContainer, VoucherContainer, VoucherSubtitle, VoucherTitle } from './VoucherDrawer.style'
import { FormControl } from 'parkdepot-shared/components/FormControl'
import Input from 'parkdepot-shared/components/Input/Input'
import Button from 'parkdepot-shared/components/Buttons/Button'
import { BUTTON_KIND, BUTTON_SIZE } from 'parkdepot-shared/components/Buttons'
import useApplyCarpayVoucher from '@hooks/useApplyCarpayVoucher'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import Spinner from 'parkdepot-shared/components/Spinner'
import VoucherSuccess from './VoucherSuccess.component'
import { getFragmentData } from 'parkdepot-shared/gql'
import { OrderResponseFragment } from '@graphql/queries/getOpenOrders.query'
import VoucherErrored from './VoucherErrored.component'
import useCarpayPaymentFlow from '@hooks/useCarpayPaymentFlow'
import { useTranslation } from 'react-i18next'
import { B2COrderResponseFragment } from 'parkdepot-shared/gql/graphql'

interface VoucherContentProps {
    loading: boolean
    code: string
    onCodeChange: (code: string) => void
    onCodeApply: () => void
    voucherSuccess: boolean
    voucherErrored: boolean
    showErrorOverlay: boolean
}

const VoucherContent = ({
    loading,
    code,
    onCodeApply,
    onCodeChange,
    voucherSuccess,
    voucherErrored,
    showErrorOverlay
}: VoucherContentProps) => {
    const { t } = useTranslation()

    return (
        <>
            <VoucherContainer $loading={loading || voucherSuccess || showErrorOverlay}>
                <VoucherTitle>{t('carpay.voucher.title')}</VoucherTitle>
                <VoucherSubtitle>{t('carpay.voucher.subtitle')}</VoucherSubtitle>

                <div>
                    <FormControl
                        label={t('carpay.voucher.code')}
                        error={voucherErrored ? t('carpay.voucher.error') : null}
                    >
                        <Input
                            id="voucher-input"
                            value={code}
                            onChange={(e) => onCodeChange(e.target.value.toUpperCase())}
                            placeholder="1X7P82FG"
                            error={voucherErrored}
                        />
                    </FormControl>
                </div>

                <Button
                    id="apply-code-drawer-button"
                    $kind={BUTTON_KIND.primary}
                    $size={BUTTON_SIZE.medium}
                    disabled={!code || loading}
                    onClick={onCodeApply}
                >
                    {t('carpay.voucher.applyButton')}
                </Button>
            </VoucherContainer>

            <OverlayContainer $appearence={voucherSuccess}>
                <VoucherSuccess />
            </OverlayContainer>

            <OverlayContainer $appearence={showErrorOverlay}>
                <VoucherErrored />
            </OverlayContainer>

            <OverlayContainer $appearence={loading}>
                <Spinner theme="primary" size="xl" />
            </OverlayContainer>
        </>
    )
}

interface VoucherDrawerProps {
    orderData: B2COrderResponseFragment
    isOpen: boolean
    onClose: () => void
    disabled: boolean
}

const VoucherDrawer = ({ orderData, isOpen, onClose, disabled }: VoucherDrawerProps) => {
    const { search } = useLocation()
    const history = useHistory()
    const { order_id } = orderData.order
    const [showErrorOverlay, setShowErrorOverlay] = useState(false)
    const { state } = useCarpayPaymentFlow()

    const [applyCarpayVoucher, { loading, data, error }] = useApplyCarpayVoucher({
        refetchQueries: ['B2CGetOrderById', 'B2CGetOrderPrice'],
        awaitRefetchQueries: true,
        onError: () => {
            setShowErrorOverlay(true)

            setTimeout(() => {
                setShowErrorOverlay(false)
            }, 1000)
        },
        onCompleted: (data) => {
            setTimeout(() => {
                onClose()

                const orderResponse = getFragmentData(OrderResponseFragment, data.b2c_apply_discount)
                if (orderResponse.price.discounted_parking_price === 0) {
                    history.replace(`/carpay/orders/${order_id}/payment-finished?status=Authorised`)
                }
            }, 1000)
        }
    })

    const [code, setCode] = useState(() => {
        if (disabled) return ''

        let couponCode = new URLSearchParams(search).get('c')
        // couponCode is base64 encoded and might contain of '+' and '/'.
        // Since '+' is replaced to ' ' in URLSearchParams.get we have to replace it back.
        // We have to do nothing with '/'
        couponCode = couponCode ? couponCode.replace(/ /g, '+') : ''

        if (couponCode && order_id) {
            // This is executed twice on development cause of strict mode
            applyCarpayVoucher({
                variables: {
                    code: couponCode,
                    order_id,
                    selected_parking_tariff: state.parkingTariff,
                    parking_context_uuid: state.parkingContextUuid
                }
            })
        }

        return couponCode ?? ''
    })

    const codeSuccess = !!data
    const codeErrored = !!error

    const handleVoucherApply = () => {
        applyCarpayVoucher({
            variables: {
                code,
                order_id,
                selected_parking_tariff: state.parkingTariff,
                parking_context_uuid: state.parkingContextUuid
            }
        })
    }

    return (
        <Drawer
            isOpen={isOpen}
            onClose={!loading ? onClose : () => null}
            content={
                <VoucherContent
                    onCodeApply={handleVoucherApply}
                    onCodeChange={(code) => setCode(code)}
                    loading={loading}
                    code={code}
                    voucherSuccess={codeSuccess}
                    voucherErrored={codeErrored}
                    showErrorOverlay={showErrorOverlay}
                />
            }
        />
    )
}

export default VoucherDrawer
